import React from 'react';
import { Box, Card, CardContent, Typography, Skeleton } from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';

export interface MetricCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  change?: number;
  subtitle?: string;
  loading?: boolean;
  format?: 'number' | 'percent' | 'currency';
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  icon,
  color,
  change,
  subtitle,
  loading = false,
  format = 'number',
}) => {
  const formatValue = (val: number | string): string => {
    if (typeof val === 'string') return val;
    switch (format) {
      case 'percent':
        return `${val}%`;
      case 'currency':
        return `$${val.toLocaleString()}`;
      default:
        return val.toLocaleString();
    }
  };

  if (loading) {
    return (
      <Card
        sx={{
          height: '100%',
          boxShadow:
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Skeleton width="60%" height={20} />
              <Skeleton width="40%" height={40} sx={{ mt: 1 }} />
              <Skeleton width="80%" height={16} sx={{ mt: 1 }} />
            </Box>
            <Skeleton variant="circular" width={48} height={48} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        boxShadow:
          '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        border: '1px solid #e5e7eb',
        borderRadius: 2,
        transition: 'box-shadow 0.2s ease-in-out',
        '&:hover': {
          boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#6b7280',
                mb: 0.5,
                textTransform: 'none',
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                fontSize: '1.875rem',
                fontWeight: 700,
                color: '#111827',
                lineHeight: 1.2,
              }}
            >
              {formatValue(value)}
            </Typography>
            {change !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                {change >= 0 ? (
                  <TrendingUpIcon
                    sx={{ fontSize: 16, color: '#10b981', mr: 0.5 }}
                  />
                ) : (
                  <TrendingDownIcon
                    sx={{ fontSize: 16, color: '#ef4444', mr: 0.5 }}
                  />
                )}
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: change >= 0 ? '#10b981' : '#ef4444',
                    fontWeight: 500,
                  }}
                >
                  {change >= 0 ? '+' : ''}
                  {change}% from last month
                </Typography>
              </Box>
            )}
            {subtitle && (
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: '#6b7280',
                  mt: 0.5,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MetricCard;
